import React from 'react';
import { DayMap } from '@/types';
import { useHooks } from './hooks';

export interface PayCalculatorProps {
  dayMap: DayMap;
  rateType: 'daily' | 'hourly';
  dailyRate: string;
  hourlyRate: string;
  onUpdateRates: (type: 'daily' | 'hourly', dailyRate: string, hourlyRate: string) => void;
  passcode: string;
}

const PayCalculator: React.FC<PayCalculatorProps> = (props) => {
  const {
    payStart,
    setPayStart,
    payEnd,
    setPayEnd,
    isPrivateMode,
    setIsPrivateMode,
    calcTotalHours,
    estimatedPay,
    formatPeso,
    handleRateTypeChange,
    handleDailyRateChange,
    handleHourlyRateChange,
    payPeriods,
    page,
    setPage,
    totalPages,
    isLoadingPeriods
  } = useHooks(props);

  const { rateType, dailyRate, hourlyRate } = props;

  return (
    <div className="space-y-6 animate-fade-in delay-100">
      <div className="glass-panel p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h2 className="text-xl font-display font-semibold tracking-tight text-foreground">
              Pay Calculator
            </h2>
            <p className="text-xs text-foreground/50 font-mono mt-0.5 uppercase tracking-wider">
              ESTIMATE COMPENSATION FOR SPECIFIED PARAMETER RANGES
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsPrivateMode(!isPrivateMode)}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-card-border/60 hover:border-accent/40 text-[10px] font-mono uppercase tracking-wider text-foreground/75 hover:text-foreground cursor-pointer transition-all bg-background/40 self-start active:scale-95 select-none"
          >
            {isPrivateMode ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Show Pay
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.076m3.607-3A9.96 9.96 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21M9 9l6 6M4 4l16 16" />
                </svg>
                Hide Pay
              </>
            )}
          </button>
        </div>

        {/* Date pickers & Rate input */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col">
            <div className="h-6 flex items-center mb-1.5">
              <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/45">
                Start Date
              </label>
            </div>
            <input
              type="date"
              value={payStart}
              onChange={(e) => setPayStart(e.target.value)}
              className="bg-background border border-card-border rounded px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40 transition-all w-full"
            />
          </div>

          <div className="flex flex-col">
            <div className="h-6 flex items-center mb-1.5">
              <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/45">
                End Date
              </label>
            </div>
            <input
              type="date"
              value={payEnd}
              onChange={(e) => setPayEnd(e.target.value)}
              className="bg-background border border-card-border rounded px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40 transition-all w-full"
            />
          </div>

          <div className="flex flex-col">
            <div className="h-6 flex justify-between items-center mb-1.5">
              <span className="text-[10px] uppercase font-mono tracking-wider text-foreground/45">
                {rateType === 'daily' ? 'Daily Rate (₱)' : 'Hourly Rate (₱)'}
              </span>
              <div className="flex bg-background border border-card-border rounded p-0.5 text-[8px] font-mono">
                <button
                  type="button"
                  onClick={() => handleRateTypeChange('daily')}
                  className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                    rateType === 'daily' ? 'bg-accent text-black font-semibold' : 'text-foreground/50 hover:text-foreground'
                  }`}
                >
                  Daily
                </button>
                <button
                  type="button"
                  onClick={() => handleRateTypeChange('hourly')}
                  className={`px-1.5 py-0.5 rounded transition-all cursor-pointer ${
                    rateType === 'hourly' ? 'bg-accent text-black font-semibold' : 'text-foreground/50 hover:text-foreground'
                  }`}
                >
                  Hourly
                </button>
              </div>
            </div>
            <input
              type="number"
              min="0"
              value={rateType === 'daily' ? dailyRate : hourlyRate}
              onChange={(e) => rateType === 'daily' ? handleDailyRateChange(e.target.value) : handleHourlyRateChange(e.target.value)}
              className={`bg-background border border-card-border rounded px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40 transition-all w-full ${
                isPrivateMode ? 'blur-[8px] select-none pointer-events-none' : ''
              }`}
              placeholder={rateType === 'daily' ? 'e.g. 1000' : 'e.g. 125'}
            />
          </div>
        </div>

        {/* Metric Outputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-card-border/50">
          {/* Total Hours Metric */}
          <div className="bg-background/20 border border-card-border/50 rounded-lg p-5 flex flex-col justify-between">
            <span className="text-[10px] uppercase font-mono tracking-wider text-foreground/40">
              Total Hours in Range
            </span>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-3xl font-display font-semibold tracking-tight text-foreground num-mono">
                {calcTotalHours.toFixed(2)}
              </span>
              <span className="text-xs font-mono text-foreground/50 uppercase">hrs</span>
            </div>
            <p className="text-[9px] text-foreground/30 font-mono mt-1 uppercase tracking-wider">
              Sum of duty sessions inside interval
            </p>
          </div>

          {/* Estimated Pay Metric */}
          <div className="bg-background/20 border border-card-border/50 rounded-lg p-5 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 h-[2px] w-12 bg-accent"></div>
            <span className="text-[10px] uppercase font-mono tracking-wider text-foreground/40">
              Estimated Net Compensation
            </span>
            <div className="mt-2">
              <span className={`text-3xl font-display font-semibold tracking-tight text-accent num-mono transition-all duration-300 ${
                isPrivateMode ? 'blur-[12px] select-none pointer-events-none' : ''
              }`}>
                {formatPeso(estimatedPay)}
              </span>
            </div>
            <p className="text-[9px] text-foreground/30 font-mono mt-1 uppercase tracking-wider">
              {rateType === 'daily' ? '(Total Hours / 8) × Daily Rate' : 'Total Hours × Hourly Rate'}
            </p>
          </div>
        </div>

        {/* Standard Pay Cycles */}
        <div className="pt-5 border-t border-card-border/50 space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div>
              <h3 className="text-xs font-display font-semibold tracking-wider text-foreground/70 uppercase">
                All Pay Cycle Summaries
              </h3>
              <p className="text-[9px] text-foreground/45 font-mono mt-0.5 uppercase tracking-wider">
                AUTO-COMPUTED SEMI-MONTHLY PERIODS (PAGINATED SERVER-SIDE)
              </p>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2 bg-background/30 border border-card-border/50 rounded p-1 text-[10px] font-mono">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded hover:bg-background/50 hover:text-foreground text-foreground/60 transition-all cursor-pointer disabled:opacity-30 disabled:pointer-events-none"
                >
                  Prev
                </button>
                <span className="px-2 text-foreground/80">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="px-2 py-1 rounded hover:bg-background/50 hover:text-foreground text-foreground/60 transition-all cursor-pointer disabled:opacity-30 disabled:pointer-events-none"
                >
                  Next
                </button>
              </div>
            )}
          </div>
          
          {isLoadingPeriods ? (
            <div className="text-center py-8 text-[10px] font-mono text-foreground/40 uppercase tracking-widest animate-pulse">
              LOADING PERIODS...
            </div>
          ) : payPeriods.length === 0 ? (
            <div className="text-center py-8 text-[10px] font-mono text-foreground/30 uppercase tracking-widest">
              NO PAY CYCLE HISTORY FOUND
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in">
              {payPeriods.map((period, idx) => {
                const isCurrent = page === 1 && idx === 0;

                return (
                  <div 
                    key={`${period.start}-${period.end}`}
                    onClick={() => {
                      setPayStart(period.start);
                      setPayEnd(period.end);
                    }}
                    className={`border rounded-lg p-4 flex flex-col justify-between bg-background/10 transition-all hover:bg-background/25 hover:border-accent/40 cursor-pointer group relative overflow-hidden ${
                      isCurrent ? 'border-accent/40 shadow-sm shadow-accent/5' : 'border-card-border/50'
                    }`}
                  >
                    {isCurrent && (
                      <div className="absolute top-0 right-0 bg-accent text-[8px] font-mono text-black font-semibold uppercase px-2 py-0.5 rounded-bl">
                        Current
                      </div>
                    )}
                    
                    <div className="space-y-1">
                      <span className="text-[9px] font-mono text-foreground/45 uppercase tracking-wide block">
                        {isCurrent ? 'Current Cycle' : 'Past Cycle'}
                      </span>
                      <span className="text-xs font-semibold text-foreground group-hover:text-accent transition-colors font-mono">
                        {period.label}
                      </span>
                    </div>

                    <div className="mt-4 space-y-1.5">
                      <div className="flex justify-between items-baseline text-xs">
                        <span className="text-[10px] font-mono text-foreground/40 uppercase">Hours:</span>
                        <span className="font-mono font-semibold text-foreground">{period.hours.toFixed(2)} hrs</span>
                      </div>
                      <div className="flex justify-between items-baseline border-t border-card-border/30 pt-1.5">
                        <span className="text-[10px] font-mono text-foreground/40 uppercase">Est. Pay:</span>
                        <span className={`font-mono font-bold text-accent transition-all duration-300 ${
                          isPrivateMode ? 'blur-[10px] select-none pointer-events-none' : ''
                        }`}>
                          {formatPeso(period.estimatedPay)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-[8px] font-mono text-foreground/30 text-center uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to load in calculator
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayCalculator;
